// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — Receipt Preview Modal (Redesigned)
// ─────────────────────────────────────────────────────────────────────────────
// Premium modal for previewing, printing, and downloading sales receipts and
// payment vouchers. Always renders on a white background (dark theme does NOT
// apply to the receipt content — ensures correct print output).
// ─────────────────────────────────────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import BrandLogo from '@/components/common/BrandLogo';
import { X, Download, Printer, CheckCircle2 } from 'lucide-react';
import { brandConfig } from '@/config/brandConfig';
import {
  SaleReceiptData,
  PaymentReceiptData,
  downloadPaymentReceiptPdf,
} from '@/utils/receiptGenerator';
import { exportSaleReceiptPdf, SaleWithItems } from '@/utils/pdfTemplates';
import { printHtmlContent } from '@/utils/printUtils';
import { customerPrintHtml, receiptWithPhonePrivacy } from '@/utils/customerPrintPrivacy';
import { usePrintPrivacy } from '@/hooks/usePrintPrivacy';
import { toast } from 'sonner';

export interface ReceiptPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleData?: SaleReceiptData | null;
  paymentData?: PaymentReceiptData | null;
  sale?: SaleWithItems | null;
  orgName?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRupee(val: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(val);
}

function StatusBadge({
  text,
  color,
}: {
  text: string;
  color: 'green' | 'amber' | 'red' | 'slate';
}) {
  const colorMap = {
    green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${colorMap[color]}`}
    >
      {text}
    </span>
  );
}

function paymentColor(status: string): 'green' | 'amber' | 'red' | 'slate' {
  if (status === 'paid') return 'green';
  if (status === 'partially_paid') return 'amber';
  if (status === 'unpaid') return 'red';
  return 'slate';
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ReceiptPreviewModal({
  isOpen,
  onClose,
  saleData: propSaleData,
  paymentData,
  sale,
  orgName = 'Klothiq',
}: ReceiptPreviewModalProps) {
  const { askPrivacy, privacyDialog } = usePrintPrivacy(isOpen);
  // If sale object is passed directly, construct compatible saleData for HTML preview
  const saleData: SaleReceiptData | null =
    propSaleData ??
    (sale
      ? {
          sale_number: sale.sale_number ?? 'RECEIPT',
          order_number: (sale as Record<string, unknown>).order_number as string | undefined,
          sale_date: typeof sale.sale_date === 'string' ? sale.sale_date : new Date().toISOString(),
          sale_status:
            sale.status ?? ((sale as Record<string, unknown>).sale_status as string) ?? 'completed',
          payment_status: ((sale as Record<string, unknown>).payment_status as string) ?? 'paid',
          customer: sale.customer
            ? {
                name: sale.customer_name ?? sale.customer.name ?? 'Customer',
                phone: sale.customer.phone,
                address: sale.customer.address,
                city: sale.customer.city,
                pincode: sale.customer.pincode,
              }
            : null,
          items: (sale.sale_items ?? sale.items ?? []).map((it) => ({
            name: it.product_name ?? ((it as Record<string, unknown>).name as string) ?? 'Product',
            sku: it.sku,
            size: it.size,
            color: it.color,
            quantity: Number(it.quantity ?? 1),
            unit_price: Number(it.unit_price ?? 0),
            line_total: Number(
              it.total_price ??
                (it as Record<string, unknown>).line_total ??
                Number(it.quantity ?? 1) * Number(it.unit_price ?? 0),
            ),
          })),
          subtotal: Number(
            sale.subtotal ?? (sale as Record<string, unknown>).subtotal_amount ?? sale.total ?? 0,
          ),
          delivery_charge: Number((sale as Record<string, unknown>).delivery_charge ?? 0),
          discount_total: Number(
            sale.discount ?? (sale as Record<string, unknown>).discount_amount ?? 0,
          ),
          total_amount: Number(sale.total ?? (sale as Record<string, unknown>).total_amount ?? 0),
          paid_amount: Number(
            sale.amount_paid ?? (sale as Record<string, unknown>).paid_amount ?? sale.total ?? 0,
          ),
          balance_due: sale.financial_summary
            ? (sale.financial_summary as { amount_due: number | null }).amount_due
            : sale.return_review ||
                /returned|draft|cancelled/.test(String(sale.status ?? sale.sale_status))
              ? null
              : Number(
                  (sale.total ?? sale.total_amount ?? 0) -
                    Number(sale.amount_paid ?? sale.paid_amount ?? 0),
                ),
          balance_note: /returned/.test(String(sale.status ?? sale.sale_status))
            ? 'Return adjustment requires balance review'
            : undefined,
          notes: (sale as Record<string, unknown>).notes as string | null | undefined,
        }
      : null);

  const activeSale = saleData;

  if (!isOpen || (!activeSale && !paymentData)) return null;

  const title = activeSale
    ? `Receipt ${activeSale.sale_number}`
    : `Payment ${paymentData?.receipt_number}`;

  const handleDownload = async () => {
    const masked = activeSale ? await askPrivacy() : false;
    if (masked === null) return;
    try {
      if (sale) {
        exportSaleReceiptPdf(receiptWithPhonePrivacy(sale, masked), orgName);
        toast.success(`Receipt ${sale.sale_number ?? ''} downloaded`);
      } else if (activeSale) {
        exportSaleReceiptPdf(
          receiptWithPhonePrivacy(activeSale, masked) as unknown as SaleWithItems,
          orgName,
        );
        toast.success(`Receipt ${activeSale.sale_number} downloaded`);
      } else if (paymentData) {
        downloadPaymentReceiptPdf(paymentData);
        toast.success(`Payment voucher ${paymentData.receipt_number} downloaded`);
      }
    } catch (err: unknown) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to download receipt');
    }
  };

  const handlePrint = async () => {
    const masked = activeSale ? await askPrivacy() : false;
    if (masked === null) return;
    try {
      const content = document.getElementById('klothiq-receipt-content');
      if (content) printHtmlContent(customerPrintHtml(content, masked), { documentTitle: title });
      toast.success('Print dialog opened');
    } catch (err: unknown) {
      console.error(err);
      toast.error('Failed to initiate printing');
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-sm"
        data-field-guide-scope
      >
        <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-card shadow-2xl overflow-hidden">
          {/* ── Modal Header ── */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-muted/40 no-print">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <div className="flex items-start justify-between gap-3 pr-8">
                <h3 className="text-base font-bold text-foreground">{title}</h3>
                <UserGuide compact />
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* ── Receipt Preview Body ── */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-100">
            {/*
            ⚠️ The receipt div MUST use hardcoded white/slate colors.
            No dark: CSS variants here — dark mode does NOT apply to receipts.
            This ensures consistent white background on print.
          */}
            <div
              id="klothiq-receipt-content"
              className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
              style={{ color: '#1e293b', fontFamily: 'system-ui, sans-serif' }}
            >
              {/* ── 1. Premium Ink Brand Header ── */}
              <div
                className="px-6 py-4 flex items-start justify-between relative"
                style={{ backgroundColor: '#14161F', borderBottom: '2px solid #C7A13F' }}
              >
                <div className="flex min-w-0 items-center gap-3">
                  {/* Logo */}
                  <BrandLogo className="h-10 w-10" />
                  <div className="min-w-0">
                    <p className="text-lg font-extrabold tracking-tight text-white leading-tight">
                      {brandConfig.name}
                    </p>
                    <p className="text-xs font-medium" style={{ color: '#C7A13F' }}>
                      {brandConfig.tagline}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className="inline-block rounded-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
                    style={{ backgroundColor: 'rgba(199,161,63,0.18)', color: '#D9BB63' }}
                  >
                    {saleData ? 'Sales Receipt' : 'Payment Receipt'}
                  </span>
                  <p className="mt-1.5 text-xs font-mono font-semibold text-white">
                    #{saleData ? saleData.sale_number : paymentData?.receipt_number}
                  </p>
                  <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
                    {saleData ? saleData.sale_date : paymentData?.payment_date}
                  </p>
                </div>
              </div>

              {/* ── 2. Business Contact Strip ── */}
              <div
                className="flex items-center justify-center gap-3 px-6 py-2 text-[10px] font-medium"
                style={{ backgroundColor: '#1F2230', color: '#D9BB63' }}
              >
                <span>{brandConfig.phone}</span>
                <span className="opacity-50">·</span>
                <span>
                  {brandConfig.address}, {brandConfig.cityStateZip}
                </span>
              </div>

              <div className="px-6 py-5 space-y-5">
                {/* ── 3. Sale Status Strip ── */}
                {saleData && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge text={saleData.sale_status.replace(/_/g, ' ')} color="slate" />
                    <StatusBadge
                      text={saleData.payment_status.replace(/_/g, ' ')}
                      color={paymentColor(saleData.payment_status)}
                    />
                  </div>
                )}

                {/* ── 4. Bill Details (Sale info + Customer) ── */}
                {saleData && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Customer / Bill To */}
                    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                        Bill To
                      </p>
                      <p className="text-sm font-bold text-slate-900">
                        {saleData.customer?.name || 'Walk-in Customer'}
                      </p>
                      {saleData.customer?.phone && (
                        <p data-customer-phone className="text-xs text-slate-600 mt-0.5">
                          {saleData.customer.phone}
                        </p>
                      )}
                      {(saleData.customer?.address || saleData.customer?.city) && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {[
                            saleData.customer.address,
                            saleData.customer.city,
                            saleData.customer.pincode,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                    </div>

                    {/* Sale meta */}
                    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-right">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                        Sale Details
                      </p>
                      <p className="text-xs text-slate-700">
                        <span className="text-slate-500">Date: </span>
                        {saleData.sale_date}
                      </p>
                      {saleData.payment_method && (
                        <p className="text-xs text-slate-700 mt-0.5">
                          <span className="text-slate-500">Method: </span>
                          {saleData.payment_method.toUpperCase().replace(/_/g, ' ')}
                        </p>
                      )}
                      {saleData.notes && (
                        <p className="text-xs text-slate-500 mt-0.5 italic">{saleData.notes}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* ── 5. Payment Receipt meta ── */}
                {paymentData && (
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Transaction Summary
                    </p>
                    <p className="text-sm font-bold text-slate-900">
                      {paymentData.party_name || 'General'}
                    </p>
                    <p className="text-xs text-slate-600">
                      Type: {paymentData.payment_type.toUpperCase().replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-slate-600">
                      Method: {paymentData.payment_method.toUpperCase().replace(/_/g, ' ')}
                    </p>
                    {paymentData.particular && (
                      <p className="text-xs text-slate-500">
                        Particulars: {paymentData.particular}
                      </p>
                    )}
                  </div>
                )}

                {/* ── 6. Items Table (Sales only) ── */}
                {saleData && (
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr style={{ backgroundColor: '#14161F', color: '#ffffff' }}>
                          <th className="px-3 py-2 font-semibold">Item</th>
                          <th className="px-3 py-2 text-center font-semibold w-12">Qty</th>
                          <th className="px-3 py-2 text-right font-semibold w-20">Rate</th>
                          <th className="px-3 py-2 text-right font-semibold w-22">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {saleData.items.map((item, idx) => (
                          <tr
                            key={idx}
                            style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}
                          >
                            <td className="px-3 py-2">
                              <p className="font-semibold text-slate-900">{item.name}</p>
                              <p className="text-[10px] text-slate-500 mt-0.5">
                                {[
                                  item.size ? `Size: ${item.size}` : null,
                                  item.color ? `Color: ${item.color}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                              {item.discount && item.discount > 0 && (
                                <p className="text-[10px] text-emerald-600 mt-0.5">
                                  Discount: -{formatRupee(item.discount)}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center font-medium text-slate-800">
                              {item.quantity}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-slate-700">
                              {formatRupee(item.unit_price)}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold font-mono text-slate-900">
                              {formatRupee(item.line_total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── 7. Totals Breakdown (Sales) ── */}
                {saleData && (
                  <div className="flex justify-end">
                    <div className="w-full max-w-xs rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                      <div className="divide-y divide-slate-100">
                        {/* Subtotal */}
                        <div className="flex justify-between px-4 py-2 text-xs text-slate-600">
                          <span>Subtotal</span>
                          <span className="font-mono">{formatRupee(saleData.subtotal)}</span>
                        </div>

                        {/* Delivery */}
                        {Boolean(saleData.delivery_charge) && (
                          <div className="flex justify-between px-4 py-2 text-xs text-slate-600">
                            <span>Delivery Charge</span>
                            <span className="font-mono">
                              {formatRupee(saleData.delivery_charge!)}
                            </span>
                          </div>
                        )}

                        {/* Discount */}
                        {Boolean(saleData.discount_total) && (
                          <div className="flex justify-between px-4 py-2 text-xs text-emerald-700">
                            <span>Discount</span>
                            <span className="font-mono">
                              -{formatRupee(saleData.discount_total!)}
                            </span>
                          </div>
                        )}

                        {/* Grand Total */}
                        <div
                          className="flex justify-between px-4 py-2.5 text-sm font-bold"
                          style={{ backgroundColor: '#14161F', color: '#D9BB63' }}
                        >
                          <span>Grand Total</span>
                          <span className="font-mono">{formatRupee(saleData.total_amount)}</span>
                        </div>

                        {/* Paid */}
                        <div className="flex justify-between px-4 py-2 text-xs text-emerald-700 font-semibold">
                          <span>Amount Paid</span>
                          <span className="font-mono">{formatRupee(saleData.paid_amount)}</span>
                        </div>

                        {/* Balance due */}
                        {saleData.balance_note && (
                          <p className="text-xs text-amber-800">{saleData.balance_note}</p>
                        )}
                        {(saleData.balance_due ?? 0) > 0 && (
                          <div className="flex justify-between px-4 py-2 text-xs font-bold text-red-600">
                            <span>Balance Due</span>
                            <span className="font-mono">
                              {formatRupee(saleData.balance_due ?? 0)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── 8. Payment Amount Highlight (Payment receipts) ── */}
                {paymentData && (
                  <div
                    className="rounded-lg p-5 text-center"
                    style={{ backgroundColor: '#14161F', border: '1px solid #C7A13F' }}
                  >
                    <p
                      className="text-[10px] uppercase tracking-widest font-semibold mb-1"
                      style={{ color: '#D9BB63' }}
                    >
                      Total Amount Received
                    </p>
                    <p className="text-3xl font-extrabold font-mono" style={{ color: '#C7A13F' }}>
                      {formatRupee(paymentData.amount)}
                    </p>
                  </div>
                )}

                {/* ── 9. Footer ── */}
                <div className="border-t border-slate-100 pt-4 text-center">
                  <p className="text-xs font-semibold text-slate-700">
                    {brandConfig.receiptFooterMessage}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {brandConfig.name} · {brandConfig.phone} · {brandConfig.address},{' '}
                    {brandConfig.cityStateZip}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Modal Actions ── */}
          <div className="flex items-center justify-between border-t border-border px-4 py-3 bg-muted/40 no-print">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Close
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <Printer className="h-3.5 w-3.5 text-blue-600" />
                <span>Print</span>
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {privacyDialog}
    </>
  );
}
